// رسیدگی صورت وضعیت
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '');

const API = (path, opts = {}) => fetch(`/api/evaluation${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

// رنگ ردیف‌ها بر اساس وضعیت
const ROW_COLOR = { begin: '#f8d7f8', finish: '#d6f5f7', budget: '#e8e8e8' };


/* ستون‌های خروجی اکسل — همان عنوان ستون‌های گرید */
const XL_REQUEST = [
  { key: 'accounting_kind_name', label: 'نوع حساب' },
  { key: 'contractor', label: 'پیمانکار' },
  { key: 'contract', label: 'قرارداد' },
  { key: 'work_period', label: 'دوره کارکرد' },
  { key: 'request_type_name', label: 'نوع صورت وضعیت' },
  { key: 'request_no', label: 'شماره' },
  { key: 'letter_no', label: 'نامه' },
  { key: 'letter_date', label: 'تاریخ' },
  { key: 'zemanat_performance', label: 'ضمانتنامه انجام تعهدات', num: true },
  { key: 'zemanat_prepayment', label: 'ضمانتنامه پیش پرداخت', num: true },
  { key: 'prepayment_debt', label: 'بدهی پیش پرداخت', num: true },
  { key: 'request_amount', label: 'مبلغ', num: true },
  { key: 'tax_amount', label: 'مالیات بر ارزش افزوده', num: true },
  { key: 'insure_amount', label: 'بیمه کارفرما', num: true },
  { key: 'total_amount', label: 'جمع هزینه', num: true },
  { key: 'deduction', label: 'جمع کسورات', num: true },
  { key: 'net_amount', label: 'خالص', num: true },
  { key: 'evaluation_title', label: 'نوع سند' },
  { key: 'begin_date', label: 'تاریخ شروع رسیدگی' },
  { key: 'finish_date', label: 'تاریخ خاتمه رسیدگی' },
  { key: 'budget_date', label: 'تاریخ ارسال به بودجه' },
  { key: 'remark', label: 'توضیح' },
];

export default function EvaluationRequestList({ user, onBack }) {
  const [data, setData]         = useState([]);
  const pager = usePager();          // صفحه‌بندی مشترک — ۲۰۰ ردیف در هر صفحه

  // چک‌باکس‌های فرم
  const [ready, setReady]       = useState(true);
  const [inEval, setInEval]     = useState(true);
  const [finished, setFinished] = useState(false);
  const [sent, setSent]         = useState(false);
  const [hazineh, setHazineh]   = useState(true);
  const [tamalok, setTamalok]   = useState(true);

  const [year, setYear]         = useState('');   // سال مالی سراسری
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [selDed, setSelDed]     = useState(null);
  const [docs, setDocs]         = useState([]);        // اقلام رسیدگی خود سند
  const [dedDocs, setDedDocs]   = useState([]);        // اقلام رسیدگی کسورات
  const [picked, setPicked]     = useState([]);        // شناسه‌های انتخاب‌شده برای تایید/واخواهی
  const [reason, setReason]     = useState('');
  const [history, setHistory]   = useState(null);

  const [loading, setLoading]   = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');
  const [confirmAct, setConfirmAct] = useState(null);

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const cols = ['نوع حساب', 'پیمانکار', 'قرارداد', 'دوره کارکرد', 'نوع صورت وضعیت', 'شماره',
    'نامه', 'تاریخ', 'مبلغ', 'جمع کسورات', 'خالص', 'نوع رسیدگی',
    'شروع رسیدگی', 'خاتمه رسیدگی', 'ارسال به بودجه'];
  const initW = [90, 170, 260, 150, 120, 90, 100, 100, 130, 120, 130, 140, 140, 140, 140];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const load = useCallback(async (opt = {}) => {
    setLoading(true); setError('');
    const p = new URLSearchParams({
      ready:    (opt.ready    ?? ready)    ? 1 : 0,
      in_eval:  (opt.inEval   ?? inEval)   ? 1 : 0,
      finished: (opt.finished ?? finished) ? 1 : 0,
      sent:     (opt.sent     ?? sent)     ? 1 : 0,
      hazineh:  (opt.hazineh  ?? hazineh)  ? 1 : 0,
      tamalok:  (opt.tamalok  ?? tamalok)  ? 1 : 0,
      search:   opt.search ?? search,
      page:     opt.page ?? 1,
    });
    const j = await API(`/contractor-requests/?${p.toString()}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setData(prev => pager.merge(prev, j, opt.page ?? 1));
    if ((opt.page ?? 1) === 1) {
      setSelected(null); setDeductions([]); setSelDed(null);
      setDocs([]); setDedDocs([]); setPicked([]);
    }
  });   // eslint-disable-line

  useEffect(() => {
    // سال مالی پیش‌فرض = سال جاری شمسی
    const y = new Intl.DateTimeFormat('en-u-ca-persian', { year: 'numeric' })
      .format(new Date()).replace(/[^0-9]/g, '');
    setYear(y);
    load({ page: 1 });
  }, []);   // eslint-disable-line

  // انتخاب صورت‌وضعیت → کسورات + اقلام رسیدگی
  const pick = async (r) => {
    if (selected?.id === r.id) {
      setSelected(null); setDeductions([]); setSelDed(null); setDocs([]); setDedDocs([]);
      return;
    }
    setSelected(r); setSelDed(null); setDedDocs([]); setPicked([]);
    const [d, doc] = await Promise.all([
      API(`/contractor-requests/${r.id}/deductions/`),
      API(`/contractor-requests/${r.id}/documents/`),
    ]);
    if (d.ok) setDeductions(d.data);
    if (doc.ok) setDocs(doc.data);
  };

  const pickDeduction = async (d) => {
    if (selDed?.id === d.id) { setSelDed(null); setDedDocs([]); return; }
    setSelDed(d); setPicked([]);
    const j = await API(`/deductions/${d.id}/documents/`);
    if (j.ok) setDedDocs(j.data);
  };

  const refreshDocs = async () => {
    if (!selected) return;
    const doc = await API(`/contractor-requests/${selected.id}/documents/`);
    if (doc.ok) setDocs(doc.data);
    if (selDed) {
      const j = await API(`/deductions/${selDed.id}/documents/`);
      if (j.ok) setDedDocs(j.data);
    }
  };

  const togglePick = (id) =>
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const verdict = async (isOk, ids) => {
    const list = ids ?? picked;
    if (!list.length) { showToast('قلمی انتخاب نشده است'); return; }
    if (!isOk && !reason.trim()) { showToast('برای واخواهی، توضیح رسیدگی الزامی است'); return; }
    setBusy(true);
    const j = await API('/documents/verdict/', {
      method: 'POST',
      body: JSON.stringify({ ids: list, is_ok: isOk ? 1 : 0, reason }),
    });
    setBusy(false);
    if (j.ok) { showToast(j.msg); setPicked([]); setReason(''); refreshDocs(); }
    else showToast(j.msg);
  };

  const runWorkflow = async (action) => {
    if (!selected) return;
    setBusy(true);
    const j = await API(`/contractor-requests/${selected.id}/workflow/`, {
      method: 'POST', body: JSON.stringify({ action }),
    });
    setBusy(false); setConfirmAct(null);
    if (j.ok) { showToast(j.msg); load({ page: 1 }); }
    else showToast(j.msg);
  };

  const showHistory = async (docId) => {
    const j = await API(`/documents/${docId}/history/`);
    if (j.ok) setHistory(j.data);
  };

  // عملیات مجاز منوی راست‌کلیک
  const actions = (() => {
    if (!selected) return [];
    const { begin_date: b, finish_date: f, budget_date: g } = selected;
    const has = s => (s || '').trim().length >= 10;
    if (has(g)) return [
      { key: 'cancel_to_budget', label: 'لغو ارسال به بودجه', confirm: 'آيا از لغو ارسال به بودجه مطمئن هستيد؟' },
      { key: 'to_accounting',    label: 'ارسال به تنظیم حساب' },
    ];
    if (has(f)) return [
      { key: 'cancel_finish', label: 'لغو پایان رسیدگی', confirm: 'آيا از لغو پایان رسیدگی مطمئن هستيد؟' },
      { key: 'to_budget',     label: 'ارسال به بودجه' },
    ];
    if (!has(b)) return [{ key: 'start', label: 'شروع رسیدگی' }];
    return [
      { key: 'finish', label: 'پایان رسیدگی', confirm: 'آيا از خاتمه رسیدگی مطمئن هستيد؟' },
      { key: 'cancel', label: 'لغو رسیدگی',  confirm: 'آيا از لغو رسیدگی مطمئن هستيد؟' },
    ];
  })();

  const canJudge = selected && !(selected.finish_date || '').trim();

  const chk = (label, val, set, key) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <input type="checkbox" checked={val} onChange={e => { set(e.target.checked); load({ [key]: e.target.checked, page: 1 }); }} />
      {label}
    </label>
  );

  const toolbar = (
    <>
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', whiteSpace: 'nowrap' }}>
        سال مالی
        <input value={year} onChange={e => setYear(e.target.value)}
          onBlur={() => load({ page: 1 })}
          onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
          style={ui.yearInput} />
      </label>
      <span style={{ color: '#ccc' }}>|</span>
      {chk('آماده رسیدگی', ready, setReady, 'ready')}
      {chk('در حال رسیدگی', inEval, setInEval, 'inEval')}
      {chk('رسیدگی‌شده', finished, setFinished, 'finished')}
      {chk('ارسال به بودجه', sent, setSent, 'sent')}
      <span style={{ color: '#ccc' }}>|</span>
      {chk('هزینه‌ای', hazineh, setHazineh, 'hazineh')}
      {chk('تملک', tamalok, setTamalok, 'tamalok')}
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
        style={{ ...ui.searchInput, flex: 1, minWidth: '150px' }} placeholder="جستجو (پیمانکار، قرارداد، شماره، نامه)..." />
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pager.total} loading={loading} />
      <ExcelButton title="رسیدگی صورت وضعیت" columns={XL_REQUEST} rows={data} />
    </>
  );

  const DocTable = ({ title, rows, emptyText }) => (
    <div style={{ flex: 1, minWidth: '380px' }}>
      <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#1a2f50', margin: '6px 2px' }}>{title}</div>
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={ui.table}>
          <thead><tr>
            {canJudge && <th style={{ ...ui.th, width: '36px' }}>✓</th>}
            <th style={ui.th}>وضعیت</th><th style={ui.th}>عنوان</th>
            <th style={ui.th}>تاریخ</th><th style={ui.th}>توضیح رسیدگی</th><th style={ui.th}>سابقه</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fdf3e3' }}>
                {canJudge && (
                  <td style={ui.td}>
                    <input type="checkbox" checked={picked.includes(r.id)} onChange={() => togglePick(r.id)} />
                  </td>
                )}
                <td style={{ ...ui.td, color: r.is_ok ? '#1b5e20' : (r.reject_reason ? '#b71c1c' : '#666') }}>
                  {r.status_name || '—'}
                </td>
                <td style={{ ...ui.td, textAlign: 'right' }} title={r.remark}>{r.title || '•'}</td>
                <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>
                  {r.event_date || '•'}{r.event_time ? ` ${r.event_time}` : ''}
                </td>
                <td style={{ ...ui.td, textAlign: 'right' }}>{r.reject_reason || '•'}</td>
                <td style={ui.td}>
                  {r.history_count > 0
                    ? <button onClick={() => showHistory(r.id)} style={{ ...ui.btnItems, padding: '2px 8px', fontSize: '11px' }}>{toFa(r.history_count)}</button>
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div style={{ ...ui.empty, padding: '14px' }}>{emptyText}</div>}
      </div>
    </div>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="رسیدگی صورت وضعیت" subtitle="بخش رسیدگی — سامانه ارجان"
        toolbar={toolbar} error={error}
        pager={pager} loading={loading} onMore={p => load({ page: p })}>
        {/* چیدمان دوتکه: گرید صورت‌وضعیت‌ها بالا با اسکرول خودش،
            پنل کسورات و اقلام رسیدگی پایین و همیشه در دید. */}
        <div style={ui.splitWrap}>
        <div style={ui.topPane}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50'
                : ROW_COLOR[r.row_color] || (i % 2 === 0 ? '#fff' : '#faf7ef');
              const tc  = sel ? '#fff' : '#111';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => pick(r)}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, color: tc }}>{r.accounting_kind_name || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.contractor}>{r.contractor || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.contract}>{r.contract || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, fontSize: '11px' }}>{r.work_period || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.request_type_name || '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.request_no) || '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.letter_no) || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{r.letter_date || '•'}</td>
                  <td style={{ ...ui.tdNum, color: tc }}>{fmtNum(r.request_amount)}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#fff' : '#b71c1c' }}>{fmtNum(r.deduction)}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#fff' : '#1b5e20' }}>{fmtNum(r.net_amount)}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.evaluation_title || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '10.5px', color: tc }}>{r.begin_date || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '10.5px', color: tc }}>{r.finish_date || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '10.5px', color: tc }}>{r.budget_date || '•'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 صورت‌وضعیتی با این فیلترها یافت نشد</div>}
        </div>

        <div style={ui.bottomPane}>
        {!selected && (
          <div style={{ ...ui.empty, padding: '18px' }}>
            برای دیدن کسورات و اقلام رسیدگی، یک صورت‌وضعیت را از جدول بالا انتخاب کنید
          </div>
        )}
        {selected && (
          <div style={{ padding: '6px 4px' }}>
            {/* نوار عملیات گردش کار */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
              background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '10px 12px', marginBottom: '10px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#1a2f50' }}>عملیات:</span>
              {actions.length === 0 && <span style={{ fontSize: '12px', color: '#888' }}>عملیاتی در این وضعیت تعریف نشده است</span>}
              {actions.map(a => (
                <button key={a.key} disabled={busy}
                  onClick={() => (a.confirm ? setConfirmAct(a) : runWorkflow(a.key))}
                  style={{ ...ui.btnNew, padding: '6px 14px', fontSize: '12px' }}>{a.label}</button>
              ))}
            </div>

            {/* کسورات */}
            <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#1a2f50', margin: '6px 2px' }}>
              کسورات صورت وضعیت — {toFa(deductions.length)} ردیف
            </div>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px' }}>
              <table style={ui.table}>
                <thead><tr>
                  <th style={ui.th}>عنوان کسورات</th><th style={ui.th}>درصد</th>
                  <th style={ui.th}>مبلغ</th><th style={ui.th}>گیرنده وجه</th><th style={ui.th}>حساب</th>
                </tr></thead>
                <tbody>
                  {deductions.map((d, i) => {
                    const s2 = selDed?.id === d.id;
                    return (
                      <tr key={d.id} onClick={() => pickDeduction(d)}
                        style={{ background: s2 ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#faf7ef',
                          color: s2 ? '#fff' : '#111', cursor: 'pointer' }}>
                        <td style={{ ...ui.td, textAlign: 'right', color: 'inherit' }}>{d.title || '•'}</td>
                        <td style={{ ...ui.td, color: 'inherit' }}>{toFa(d.percent)}</td>
                        <td style={{ ...ui.tdNum, color: 'inherit' }}>{fmtNum(d.amount)}</td>
                        <td style={{ ...ui.td, textAlign: 'right', color: 'inherit' }}>{d.receiver || '•'}</td>
                        <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: 'inherit' }}>{toFa(d.account_no) || '•'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {deductions.length === 0 && <div style={{ ...ui.empty, padding: '14px' }}>کسوراتی ثبت نشده است</div>}
            </div>

            {/* نوار تایید/واخواهی */}
            {canJudge && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
                background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>
                <input value={reason} onChange={e => setReason(e.target.value)} placeholder="توضیح رسیدگی (برای واخواهی الزامی است)"
                  style={{ ...ui.input, flex: 1, minWidth: '220px' }} />
                <button disabled={busy} onClick={() => verdict(true)} style={{ ...ui.btnNew, padding: '6px 14px', fontSize: '12px' }}>✔ تایید انتخاب‌شده‌ها</button>
                <button disabled={busy} onClick={() => verdict(true, [...docs, ...dedDocs].map(d => d.id))}
                  style={{ ...ui.btnItems, padding: '6px 14px', fontSize: '12px' }}>✔✔ تایید همه موارد</button>
                <button disabled={busy} onClick={() => verdict(false)} style={{ ...ui.btnDel, padding: '6px 14px', fontSize: '12px' }}>✕ واخواهی</button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <DocTable title="اقلام رسیدگی سند" rows={docs}
                emptyText="هنوز رسیدگی شروع نشده است" />
              <DocTable title={selDed ? `اقلام رسیدگی کسورات — ${selDed.title}` : 'اقلام رسیدگی کسورات'}
                rows={dedDocs} emptyText="یک ردیف کسورات را انتخاب کنید" />
            </div>
          </div>
        )}
        </div>
        </div>
      </ListPage>

      {confirmAct && (
        <div style={ui.overlay} onClick={() => setConfirmAct(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#e65100,#ef6c00)' }}>
              <div style={ui.mTitle}>⚠️ هشدار</div>
              <button onClick={() => setConfirmAct(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>{confirmAct.confirm}</div>
            <div style={ui.mFooter}>
              <button onClick={() => setConfirmAct(null)} style={ui.btnCancel}>خیر</button>
              <button onClick={() => runWorkflow(confirmAct.key)} disabled={busy} style={ui.btnSave}>
                {busy ? '⏳...' : 'بلی'}
              </button>
            </div>
          </div>
        </div>
      )}

      {history && (
        <div style={ui.overlay} onClick={() => setHistory(null)}>
          <div style={{ ...ui.modal, maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>🕘 سوابق رسیدگی</div>
              <button onClick={() => setHistory(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              <table style={ui.table}>
                <thead><tr>
                  <th style={ui.th}>تاریخ</th><th style={ui.th}>زمان</th>
                  <th style={ui.th}>وضعیت</th><th style={ui.th}>توضیح رسیدگی</th>
                </tr></thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fdf3e3' }}>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{h.event_date}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{h.event_time}</td>
                      <td style={{ ...ui.td, color: h.status_name === 'تایید' ? '#1b5e20' : '#b71c1c' }}>{h.status_name}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>{h.reject_reason || '•'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {history.length === 0 && <div style={ui.empty}>سابقه‌ای ثبت نشده است</div>}
            </div>
            <div style={ui.mFooter}><button onClick={() => setHistory(null)} style={ui.btnCancel}>بستن</button></div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}

